/** @format */

import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toast } from "react-toastify";
import moment from "moment";
import { invokeApi, HTTP_METHODS } from "../../utils/http-service";
import { CF_APPROVAL_HOSTNAME } from "../../../common-utilities/utils/end-points";
import { REST_URLS } from "../../../common-utilities/utils/end-points/cf-approval";
import CustomModal from "../../../common-utilities/core/modal";
import ConfirmModal from "../../../common-utilities/core/confirm-modal";
import MuiTable from "../../../common-utilities/core/mui-table";

const LIMIT = 20;

const getHeaderConfig = (handleAction) => [
  { label: "Name", id: "name" },
  {
    label: "Description",
    id: "description",
    render: (data) => data.description || "—",
  },
  {
    label: "Client ID",
    id: "clientId",
    render: (data) => (
      <span style={{ fontFamily: "monospace" }}>{data.clientId}</span>
    ),
  },
  {
    label: "Status",
    id: "isActive",
    render: (data) => (
      <Chip
        label={data.isActive ? "Active" : "Revoked"}
        color={data.isActive ? "success" : "error"}
        size="small"
      />
    ),
  },
  {
    label: "Last Used",
    id: "lastUsedAt",
    render: (data) =>
      data.lastUsedAt
        ? moment(data.lastUsedAt).format("YYYY-MM-DD HH:mm:ss A")
        : "Never",
  },
  {
    label: "Created",
    id: "createdAt",
    render: (data) =>
      data.createdAt
        ? moment(data.createdAt).format("YYYY-MM-DD HH:mm:ss A")
        : "—",
  },
  {
    label: "Actions",
    id: "actions",
    render: (data) => (
      <Stack direction="row" spacing={1} justifyContent="center">
        {data.isActive ? (
          <Button
            size="small"
            color="error"
            variant="outlined"
            onClick={() => handleAction(data, "REVOKE")}
          >
            Revoke
          </Button>
        ) : (
          <Button
            size="small"
            color="success"
            variant="outlined"
            onClick={() => handleAction(data, "REACTIVATE")}
          >
            Reactivate
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          onClick={() => handleAction(data, "ROTATE")}
        >
          Rotate Secret
        </Button>
      </Stack>
    ),
  },
];

const ApiClients = () => {
  const [data, setData] = useState({ result: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const [createModal, setCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", description: "" });
  const [creating, setCreating] = useState(false);

  // { type: "REVOKE" | "REACTIVATE" | "ROTATE", client }
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // { name, clientId, clientSecret } — shown once right after create/rotate.
  const [secretModal, setSecretModal] = useState(null);

  useEffect(() => {
    loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const loadData = (pageToLoad) => {
    setLoading(true);
    invokeApi(
      HTTP_METHODS.GET,
      `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2_API_CLIENTS}`,
      null,
      { page: pageToLoad, limit: LIMIT },
    )
      .then((response) => {
        setData({
          result: response?.result || [],
          total: response?.total || 0,
        });
      })
      .catch(() => toast.error("Failed to load API clients"))
      .finally(() => setLoading(false));
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const handleCreate = () => {
    if (!createForm.name?.trim()) {
      toast.error("Client name is required");
      return;
    }
    setCreating(true);
    invokeApi(HTTP_METHODS.POST, `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2_API_CLIENTS}`, {
      name: createForm.name.trim(),
      description: createForm.description?.trim() || undefined,
    })
      .then((response) => {
        toast.success("API client created successfully");
        setCreateModal(false);
        setCreateForm({ name: "", description: "" });
        setSecretModal({
          name: response.name,
          clientId: response.clientId,
          clientSecret: response.clientSecret,
        });
        loadData(page);
      })
      .catch((err) => toast.error(err?.message || "Failed to create API client"))
      .finally(() => setCreating(false));
  };

  const handleAction = (client, type) => {
    setConfirmAction({ type, client });
  };

  const confirmLabel = () => {
    if (!confirmAction) return "";
    const { type, client } = confirmAction;
    if (type === "REVOKE")
      return `Revoke API client "${client.name}"? Existing requests using its secret will start failing immediately.`;
    if (type === "REACTIVATE")
      return `Reactivate API client "${client.name}"? It will be able to authenticate again.`;
    if (type === "ROTATE")
      return `Rotate secret for "${client.name}"? The old secret will stop working immediately.`;
    return "";
  };

  const runConfirmedAction = () => {
    if (!confirmAction) return;
    const { type, client } = confirmAction;
    setActionLoading(true);

    let method = HTTP_METHODS.PUT;
    let path = "";
    if (type === "REVOKE") path = `${REST_URLS.CF_V2_API_CLIENTS}/${client.id}/revoke`;
    if (type === "REACTIVATE") path = `${REST_URLS.CF_V2_API_CLIENTS}/${client.id}/reactivate`;
    if (type === "ROTATE") {
      method = HTTP_METHODS.POST;
      path = `${REST_URLS.CF_V2_API_CLIENTS}/${client.id}/rotate-secret`;
    }

    invokeApi(method, `${CF_APPROVAL_HOSTNAME}${path}`)
      .then((response) => {
        if (type === "REVOKE") toast.success("API client revoked");
        if (type === "REACTIVATE") toast.success("API client reactivated");
        if (type === "ROTATE") {
          toast.success("Secret rotated successfully");
          setSecretModal({
            name: response.name || client.name,
            clientId: response.clientId || client.clientId,
            clientSecret: response.clientSecret,
          });
        }
        setConfirmAction(null);
        loadData(page);
      })
      .catch((err) => toast.error(err?.message || "Action failed"))
      .finally(() => setActionLoading(false));
  };

  const pageCount = Math.max(1, Math.ceil((data.total || 0) / LIMIT));

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="contained" onClick={() => setCreateModal(true)}>
          + Create Client
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <MuiTable
          columnsList={getHeaderConfig(handleAction)}
          dataList={data.result}
          pageCount={pageCount}
          onChange={(p) => setPage(p)}
          filters={{ page }}
        />
      )}

      {/* Create modal */}
      {createModal && (
        <CustomModal onClose={() => setCreateModal(false)} title="Create API Client">
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Name"
              value={createForm.name}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, name: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Description (optional)"
              value={createForm.description}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, description: e.target.value }))}
              fullWidth
              size="small"
              multiline
              minRows={2}
            />
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={() => setCreateModal(false)}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleCreate} disabled={creating}>
                {creating ? <CircularProgress size={20} /> : "Create"}
              </Button>
            </Stack>
          </Box>
        </CustomModal>
      )}

      {/* Confirm dialog shared by Revoke / Reactivate / Rotate Secret */}
      {confirmAction && (
        <ConfirmModal
          secondaryLabel={confirmLabel()}
          onYes={runConfirmedAction}
          onNo={() => (!actionLoading ? setConfirmAction(null) : null)}
        />
      )}

      {/* One-time secret reveal — shown right after create/rotate, never again. */}
      {secretModal && (
        <CustomModal onClose={() => setSecretModal(null)} title="API Client Secret">
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body2" color="error" fontWeight="bold">
              Copy this secret now — it will not be shown again.
            </Typography>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Client ID
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  value={secretModal.clientId}
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true, sx: { fontFamily: "monospace" } }}
                />
                <IconButton onClick={() => handleCopy(secretModal.clientId)} title="Copy Client ID">
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Client Secret
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  value={secretModal.clientSecret}
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true, sx: { fontFamily: "monospace" } }}
                />
                <IconButton onClick={() => handleCopy(secretModal.clientSecret)} title="Copy Client Secret">
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
            <Stack direction="row" justifyContent="flex-end">
              <Button variant="contained" onClick={() => setSecretModal(null)}>
                Done, I've copied it
              </Button>
            </Stack>
          </Box>
        </CustomModal>
      )}
    </Box>
  );
};

export default ApiClients;
